import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSiteContent } from "../contexts/SiteContentContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Check } from "lucide-react";

const TYPES = [
  { value: "evg", label: "EVG / EVJF" },
  { value: "mariage", label: "Mariage" },
  { value: "vin_honneur", label: "Vin d'honneur" },
  { value: "anniversaire", label: "Anniversaire" },
  { value: "entreprise", label: "Soirée entreprise" },
  { value: "autre", label: "Autre" },
];

export default function PrivateEvent() {
  const { user } = useAuth();
  const { c } = useSiteContent();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({
    event_type: "", num_guests: 30, location: "",
    preferred_date: "", package_id: "", message: "",
    contact_first_name: "", contact_last_name: "",
    contact_email: "", contact_phone: "",
    newsletter_opt_in: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  // Prefill contact fields from logged-in user
  useEffect(() => {
    if (user) {
      const parts = (user.name || "").split(" ");
      const first = parts[0] || "";
      const last = parts.slice(1).join(" ") || "";
      setForm((p) => ({
        ...p,
        contact_first_name: p.contact_first_name || first,
        contact_last_name: p.contact_last_name || last,
        contact_email: p.contact_email || user.email || "",
        contact_phone: p.contact_phone || user.phone || "",
      }));
    }
  }, [user]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Veuillez vous connecter pour envoyer votre demande.");
      nav("/connexion?next=/evenement-prive");
      return;
    }
    if (!form.event_type || !form.location || !form.preferred_date || !form.contact_first_name || !form.contact_last_name || !form.contact_email) {
      return toast.error("Merci de compléter tous les champs requis.");
    }
    setSubmitting(true);
    try {
      await api.post("/private-events", {
        ...form,
        num_guests: parseInt(form.num_guests, 10),
        package_id: form.package_id || null,
      });
      toast.success("Demande envoyée ! Vous recevrez votre devis sous 48h.");
      nav("/compte");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="private-event-page">
      <div className="mb-12 max-w-3xl">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Bar mobile</div>
        <h1 className="font-display text-5xl lg:text-6xl mb-4">Privatisez le bar pour votre événement</h1>
        <p className="text-muted-foreground leading-relaxed">
          EVG, mariage, vin d'honneur, soirée d'entreprise… Notre brigade se déplace avec un bar mobile premium et compose une carte sur mesure pour votre événement.
        </p>
      </div>

      {/* ————————————— Section Services 2 colonnes (Kintsugi) ————————————— */}
      {(() => {
        const servicesTitle = c("private_services_title", "Nos services");
        const servicesList = c("private_services_list", "").split("\n").map((s) => s.trim()).filter(Boolean);
        const tagline = c("private_services_tagline", "L'art du cocktail,\nchez vous.");
        if (servicesList.length === 0 && !tagline) return null;
        return (
          <section
            className="relative mb-16 lg:mb-24 py-14 lg:py-20 overflow-hidden"
            data-testid="private-services-section"
          >
            {/* Veines Kintsugi horizontales lumineuses en haut et bas de la section */}
            <div className="kintsugi-divider" style={{ maxWidth: "100%", position: "absolute", top: 0, left: 0, right: 0, margin: 0 }} />
            <div className="kintsugi-divider" style={{ maxWidth: "100%", position: "absolute", bottom: 0, left: 0, right: 0, margin: 0 }} />

            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-10 lg:gap-16 items-stretch">
              {/* Colonne gauche : titre + liste small-caps */}
              <div className="flex flex-col justify-center" data-testid="private-services-list">
                <div className="smallcaps-label mb-5" style={{ fontSize: "0.7rem" }}>
                  {servicesTitle}
                </div>
                <div className="kintsugi-divider" style={{ margin: "0 0 2rem 0", maxWidth: "5rem" }} />
                <ul className="space-y-4">
                  {servicesList.map((item, i) => (
                    <li
                      key={`svc-${i}`}
                      className="smallcaps-label transition-all hover:tracking-[0.32em]"
                      style={{ fontSize: "0.95rem", letterSpacing: "0.24em", color: "#D99533" }}
                      data-testid={`private-service-item-${i}`}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Séparateur central : veine Kintsugi verticale animée (desktop only) */}
              <div className="hidden md:block kintsugi-vein-v" aria-hidden="true" />

              {/* Colonne droite : phrase cursive Great Vibes avec dégradé molten animé + halo magma */}
              <div className="flex items-center justify-start md:pl-6 magma-halo" data-testid="private-services-tagline">
                <div
                  className="molten-flow font-vibes leading-[1.05] whitespace-pre-line"
                  style={{
                    fontSize: "clamp(2.75rem, 6.5vw, 5rem)",
                    textShadow: "0 0 32px rgba(245, 195, 112, 0.35)",
                  }}
                >
                  {tagline}
                </div>
              </div>
            </div>
          </section>
        );
      })()}
      {/* ——————————————————————————————————————————————————————————————— */}

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
        <div data-testid="packages-list">
          <h2 className="font-display text-3xl mb-6">Nos formules</h2>
          <div className="space-y-4">
            {packages.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => set("package_id", form.package_id === p.id ? "" : p.id)}
                className={`w-full text-left p-6 rounded-xl border transition-all ${
                  form.package_id === p.id
                    ? "border-primary bg-primary/5"
                    : "border-border bg-card hover:border-primary/30"
                }`}
                data-testid={`package-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h3 className="font-display text-2xl">{p.name}</h3>
                  <div className="text-right shrink-0">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">À partir de</div>
                    <div className="font-display text-2xl gold-text">{p.price_from.toFixed(0)} €</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{p.description}</p>
                <ul className="space-y-1.5">
                  {p.includes.map((it, i) => (
                    <li key={`${p.id || p.name}-inc-${i}`} className="text-xs text-muted-foreground flex items-center gap-2">
                      <Check className="w-3 h-3 text-primary" /> {it}
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-muted-foreground mt-3">Durée : {p.duration_hours}h</div>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="rounded-xl border border-border bg-card p-6 lg:p-8 lg:sticky lg:top-28" data-testid="private-event-form">
          <h2 className="font-display text-3xl mb-6">Demande de devis</h2>
          <div className="space-y-5">
            <div>
              <Label>Type d'événement *</Label>
              <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                <SelectTrigger className="bg-input mt-2" data-testid="event-type-trigger"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`event-type-${t.value}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="guests">Nombre d'invités *</Label>
                <Input id="guests" type="number" min={1} value={form.num_guests} onChange={(e) => set("num_guests", e.target.value)} className="bg-input mt-2" data-testid="private-guests" />
              </div>
              <div>
                <Label htmlFor="pdate">Date souhaitée *</Label>
                <Input id="pdate" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} required className="bg-input mt-2" data-testid="private-date" />
              </div>
            </div>

            <div>
              <Label htmlFor="loc">Lieu (ville / adresse) *</Label>
              <Input id="loc" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Paris, château, salle de réception…" required className="bg-input mt-2" data-testid="private-location" />
            </div>

            <div className="pt-4 border-t border-border">
              <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4">Vos coordonnées</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fname">Prénom *</Label>
                  <Input id="fname" value={form.contact_first_name} onChange={(e) => set("contact_first_name", e.target.value)} required className="bg-input mt-2" data-testid="private-firstname" />
                </div>
                <div>
                  <Label htmlFor="lname">Nom *</Label>
                  <Input id="lname" value={form.contact_last_name} onChange={(e) => set("contact_last_name", e.target.value)} required className="bg-input mt-2" data-testid="private-lastname" />
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} required placeholder="pour recevoir votre devis" className="bg-input mt-2" data-testid="private-email" />
              </div>

              <div className="mt-4">
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+33 …" className="bg-input mt-2" data-testid="private-phone" />
              </div>
            </div>

            <div>
              <Label htmlFor="msg">Votre projet</Label>
              <Textarea id="msg" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Décrivez l'événement, ambiance souhaitée, demandes spéciales…" className="bg-input mt-2 min-h-[120px]" data-testid="private-message" />
            </div>

            {form.package_id && (
              <div className="text-xs text-muted-foreground" data-testid="selected-package">
                Formule sélectionnée : {packages.find((p) => p.id === form.package_id)?.name}
              </div>
            )}

            <label className="flex items-start gap-3 p-3 rounded-lg border border-border bg-input cursor-pointer hover:border-primary/50 transition-colors" data-testid="newsletter-opt-in-label">
              <Checkbox
                checked={form.newsletter_opt_in}
                onCheckedChange={(v) => set("newsletter_opt_in", !!v)}
                data-testid="newsletter-opt-in"
              />
              <div className="flex-1">
                <div className="text-sm">Je souhaite recevoir la newsletter</div>
                <div className="text-xs text-muted-foreground">Événements à venir, ateliers et nouveautés de la carte. Désinscription à tout moment.</div>
              </div>
            </label>

            <Button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12" data-testid="private-submit-btn">
              {submitting ? "Envoi…" : "Envoyer la demande"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
